import React, { useCallback, useEffect, useMemo } from 'react'
import Button from 'src/components/base/Button'
import Input from 'src/components/base/Input'
import Checkbox from 'src/components/base/Checkbox'
import InputMultiField from 'src/components/base/InputMultiField'
import { Product } from 'src/types'
import ColorField from 'src/components/configuration/editor/defaultFields/colorField/ColorField'
import useExtractProductFromConfig from 'src/hooks/useExtractProductFromConfig'
import SelectInput from 'src/components/base/SelectInput'

import { useConfig } from '../ConfigContext'
import { INITIAL_PRODUCT } from '../constants'
import './ManageProductSection.scss'

export interface ManageProductSectionProps {
  products: Product[]
  selectedProductGroup: string[]
  // Recipe-style updater that mutates the draft products array using immer in the parent
  setProducts: (recipe: (draft: Product[]) => void) => void
  holderColor?: string
}

function ManageProductSection({ products, setProducts, selectedProductGroup, holderColor }: ManageProductSectionProps) {
  const { config } = useConfig()
  const { productGroupMap } = useExtractProductFromConfig(config)
  const productLists = useMemo(() => {
    return (
      Object.keys(productGroupMap)
        ?.map(key => productGroupMap[key])
        ?.reduce((acc, val) => acc.concat(val), []) ?? []
    )
  }, [productGroupMap])
  const getProductOptions = useCallback(
    (groupName: string) => {
      return productGroupMap[groupName]
    },
    [productGroupMap]
  )
  const onAddProduct = (groupName: string) => {
    setProducts(draft => {
      draft.push({ ...INITIAL_PRODUCT, productGroup: groupName, color: holderColor || '' })
    })
  }

  return (
    <div className="products-section">
      {selectedProductGroup.map(key => {
        const productOfGroup = products.filter(p => p.productGroup === key)
        return (
          <div key={key} className="products-section-group">
            <div className="products-section-header">
              <div className="products-section-header-title">
                <label>{key}</label>
              </div>
              <Button type="button" variant="brand-tertiary" icon="fa fa-plus" onClick={() => onAddProduct(key)}>
                Add product
              </Button>
            </div>
            <div className="products-section-body">
              {!productOfGroup.length && (
                <div className="product-row">
                  <div className="product-row-group align-center">
                    <p>No products defined</p>
                  </div>
                </div>
              )}
              {productOfGroup.map(product => {
                const selectedOption = product.product ?? null
                const index = products.findIndex(
                  p => p.product === product.product && p.productGroup === product.productGroup
                )
                return (
                  <div className="product-row" key={`product-${index}`}>
                    <div className="product-row-group align-top">
                      <div className="name-color-container">
                        <SelectInput
                          label="Product"
                          id={product.product}
                          options={getProductOptions(product.productGroup)?.map(p => p.product) ?? []}
                          selectedOption={selectedOption}
                          onSelectedOptionChange={option => {
                            const predefinedProduct = productLists.find(
                              p => p.product === option && p.productGroup === product.productGroup
                            )
                            if (predefinedProduct) {
                              setProducts(draft => {
                                draft[index].product = predefinedProduct.product
                                draft[index].productUnit = predefinedProduct.productUnit
                                draft[index].exportSequenceNumber = predefinedProduct.exportSequenceNumber
                              })
                            } else {
                              setProducts(draft => {
                                draft[index].product = option
                                draft[index].exportSequenceNumber = 0
                              })
                            }
                          }}
                          required
                          flexibleWidth
                          parentStyle={{ flex: 1 }}
                          enableCustomOption
                        />
                        <ColorField
                          color={product.color ? product.color : '#000'}
                          onColorChange={color => {
                            setProducts(draft => {
                              draft[index].color = color.hex
                            })
                          }}
                          style={{ height: '48px', width: '48px' }}
                        />
                      </div>
                      <Input
                        label="Unit"
                        id={`product-unit-${index}`}
                        value={product.productUnit}
                        onChange={e => {
                          const newUnit = e.target.value
                          setProducts(draft => {
                            draft[index].productUnit = newUnit
                          })
                        }}
                        required
                        parentStyle={{ flex: 1 }}
                      />
                      <div className="product-row-group actions-container">
                        <Button
                          size="small"
                          type="button"
                          variant="neutral-secondary"
                          onClick={() => {
                            setProducts(draft => {
                              draft.splice(index, 1)
                            })
                          }}
                          icon="fa fa-trash"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="product-row-group">
                      <Input
                        label="Manning"
                        id={`manning-${index}`}
                        type="number"
                        value={product.manning}
                        onChange={e => {
                          const newManning = Number(e.target.value)
                          setProducts(draft => {
                            draft[index].manning = newManning
                          })
                        }}
                        required
                        parentStyle={{ flex: 1 }}
                      />
                      <Input
                        label="Load speed"
                        id={`load-speed-${index}`}
                        type="number"
                        value={product.loadSpeed}
                        onChange={e => {
                          const newLoadSpeed = Number(e.target.value)
                          setProducts(draft => {
                            draft[index].loadSpeed = newLoadSpeed
                          })
                        }}
                        postfix={`${product.productUnit}/hr`}
                        required
                        parentStyle={{ flex: 1 }}
                      />
                      <Checkbox
                        label="Unloading"
                        id={`is-unloading-${index}`}
                        checked={product.isUnloading}
                        onChange={e => {
                          const newIsUnloading = e.target.checked
                          setProducts(draft => {
                            draft[index].isUnloading = newIsUnloading
                          })
                        }}
                        parentStyle={{ flex: 1 }}
                      />
                    </div>
                    <div className="product-row-group">
                      <InputMultiField
                        label="Barge"
                        sublabel="Slack prior and after in hours"
                        size="sm"
                        postfix="hr"
                        input1={{
                          type: 'number',
                          value: product.slackTimes.barge.prior,
                          onChange: e => {
                            const newPrior = Number(e.target.value)
                            setProducts(draft => {
                              draft[index].slackTimes.barge.prior = newPrior
                            })
                          }
                        }}
                        input2={{
                          type: 'number',
                          value: product.slackTimes.barge.after,
                          onChange: e => {
                            const newAfter = Number(e.target.value)
                            setProducts(draft => {
                              draft[index].slackTimes.barge.after = newAfter
                            })
                          }
                        }}
                        required
                        parentStyle={{ flex: 1 }}
                      />
                      <InputMultiField
                        label="Deep sea"
                        sublabel="Slack prior and after in hours"
                        size="sm"
                        postfix="hr"
                        input1={{
                          type: 'number',
                          value: product.slackTimes.ds.prior,
                          onChange: e => {
                            const newPrior = Number(e.target.value)
                            setProducts(draft => {
                              draft[index].slackTimes.ds.prior = newPrior
                            })
                          }
                        }}
                        input2={{
                          type: 'number',
                          value: product.slackTimes.ds.after,
                          onChange: e => {
                            const newAfter = Number(e.target.value)
                            setProducts(draft => {
                              draft[index].slackTimes.ds.after = newAfter
                            })
                          }
                        }}
                        required
                        parentStyle={{ flex: 1 }}
                      />
                      <InputMultiField
                        label="Short sea"
                        sublabel="Slack prior and after in hours"
                        size="sm"
                        postfix="hr"
                        input1={{
                          type: 'number',
                          value: product.slackTimes.ss.prior,
                          onChange: e => {
                            const newPrior = Number(e.target.value)
                            setProducts(draft => {
                              draft[index].slackTimes.ss.prior = newPrior
                            })
                          }
                        }}
                        input2={{
                          type: 'number',
                          value: product.slackTimes.ss.after,
                          onChange: e => {
                            const newAfter = Number(e.target.value)
                            setProducts(draft => {
                              draft[index].slackTimes.ss.after = newAfter
                            })
                          }
                        }}
                        required
                        parentStyle={{ flex: 1 }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ManageProductSection
